/**
 * Client-side usage-tracking session id (issue #1450, part of epic #1448).
 *
 * Generated once per real login (see `frontend/src/routes/auth/callback.tsx`)
 * and held for the lifetime of the browser tab — a module-level cache backed
 * by `sessionStorage` so it survives an in-tab page reload without being
 * regenerated, but never crosses tabs or survives the tab closing.
 *
 * Exported for reuse by later usage-tracking call sites (route-change /
 * feature-click events, issue #1451) that need to tag their own events with
 * the same session.
 */

const SESSION_STORAGE_KEY = 'ea-modeling-usage-session-id';

/**
 * Superseded key from before the #1624 naming standardization (issue #1638).
 * Read-side migration only — never written again. Kept until the old key has
 * had time to fully age out of active browser tabs.
 */
const LEGACY_SESSION_STORAGE_KEY = 'ea-platform-usage-session-id';

let cachedSessionId: string | null = null;

/**
 * Migrates a session id stored under the pre-rename key to the current key,
 * so an already-active tab's usage-session continuity survives the rename
 * instead of silently resetting. Runs at most once per tab: after adoption
 * the legacy key is removed, so subsequent reads take the fast path.
 */
function migrateLegacySessionId(): string | null {
  let legacyValue: string | null = null;
  try {
    legacyValue = sessionStorage.getItem(LEGACY_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (legacyValue === null) {
    return null;
  }
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, legacyValue);
  } catch {
    // Write failed (e.g. quota exhausted) — leave the legacy key in place so
    // the value isn't lost; still adopt it in-memory for this tab's lifetime.
    return legacyValue;
  }
  try {
    sessionStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  } catch {
    // Best-effort cleanup only — a stray legacy key left behind is harmless
    // now that the new key has been written.
  }
  return legacyValue;
}

function readFromSessionStorage(): string | null {
  try {
    const currentValue = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (currentValue !== null) {
      return currentValue;
    }
  } catch {
    // sessionStorage unavailable (e.g. private-browsing storage quota) — the
    // in-memory cache still serves the rest of this tab's lifetime.
    return null;
  }
  return migrateLegacySessionId();
}

function writeToSessionStorage(sessionId: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // Best-effort persistence only — see readFromSessionStorage above.
  }
}

/**
 * Starts a new tracking session, generating and storing a fresh id. Call
 * exactly once per real login (not on every page load / session
 * rehydration) so `session.started` events map 1:1 to logins.
 */
export function startSession(): string {
  const sessionId = crypto.randomUUID();
  cachedSessionId = sessionId;
  writeToSessionStorage(sessionId);
  return sessionId;
}

/**
 * Returns the current tab's session id, or `null` if no session has been
 * started yet (e.g. usage tracking fired before login completed).
 */
export function getSessionId(): string | null {
  if (cachedSessionId) {
    return cachedSessionId;
  }
  cachedSessionId = readFromSessionStorage();
  return cachedSessionId;
}
